import { format } from 'date-fns';
import { Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';
import { cn } from '@/lib/utils';
import { formatPlanAmount, money, monthsBetween } from './pricing-format';
import { PricingTimeline } from './PricingTimeline';

const PLAN_LABELS = { starter: 'Unify Starter', pro: 'Unify Pro' };

/**
 * Builds the headline price + "£1 × 2 months, then £29/mo" lead-in from the
 * schedule timeline. The headline is the final (perpetual) phase; the subtext
 * summarises every non-trial phase before it. Returns nulls when there's no
 * timeline so the card falls back to the single `planAmount` headline.
 */
const summarisePricing = (timeline) => {
	if (!Array.isArray(timeline) || timeline.length === 0) return { headline: null, subtext: null };
	const standard = timeline[timeline.length - 1];
	const cur = standard.currency;
	const introParts = timeline
		.slice(0, -1)
		.filter((p) => !p.isTrial && p.amount != null)
		.map((p) => {
			const months = monthsBetween(p.startDate, p.endDate);
			return months ? `${formatPlanAmount(p.amount, cur)} × ${months} months` : formatPlanAmount(p.amount, cur);
		});
	const subtext = introParts.length ? `${introParts.join(', ')}, then ${formatPlanAmount(standard.amount, cur)}/mo` : null;
	return { headline: money(standard.amount, cur) || null, subtext };
};

const planStatusLabel = ({ isTrialing, cancelAt, expired }) => {
	if (expired) return 'Paused';
	if (cancelAt) return `Cancelling ${format(new Date(cancelAt), 'd MMM yyyy')}`;
	if (isTrialing) return 'Free trial active';
	return 'Active';
};

/**
 * Subscription card. Reads from `useBillingStatus` (NEA-5710). The plan label
 * uses the customer-facing `displayPlan` (NEA-5913): the migration cohort sits
 * on Pro at a reduced rate but is shown as "Unify Starter" with a "Free Pro
 * upgrade" badge (`proAccessIncluded`). The phased pricing schedule renders
 * below the headline via `PricingTimeline`.
 */
export function PlanCard({ billing, planAmount, planInterval, planCurrency, timeline, onChangePlan, onAddCard }) {
	const { plan, displayPlan, proAccessIncluded, status, isTrialing, trialEnd, currentPeriodEnd, cancelAt, expired, hasPaymentMethod } =
		billing ?? {};

	const planLabel = PLAN_LABELS[displayPlan] ?? PLAN_LABELS[plan] ?? 'Unify';
	const cadence = planInterval === 'year' ? 'year' : 'month';
	const cadenceLabel = planInterval === 'year' ? 'Yearly' : 'Monthly';
	const hasTimeline = Array.isArray(timeline) && timeline.length > 0;

	const { headline, subtext } = summarisePricing(timeline);
	const headlineAmount = headline ?? formatPlanAmount(planAmount, planCurrency);

	return (
		<section>
			<SectionHeader
				eyebrow="Plan"
				title="Subscription"
				sub="Your active Unify plan, billed monthly to the card on file."
				rightSlot={
					onChangePlan && status !== 'no_subscription' ? (
						<Button variant="outline" onClick={onChangePlan}>
							Change plan
						</Button>
					) : null
				}
			/>
			<SoftCard className={cn('!p-5 border-border', expired ? 'opacity-80' : null)}>
				{status === 'no_subscription' ? (
					<NoSubscriptionState onChangePlan={onChangePlan} />
				) : (
					<div className="space-y-4">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="min-w-0 space-y-1">
								<div className="flex flex-wrap items-center gap-2">
									<div className="text-base font-bold text-foreground">{planLabel}</div>
									<PlanBadge proAccessIncluded={proAccessIncluded} displayPlan={displayPlan} />
								</div>
								{hasTimeline ? (
									<div className="text-xs text-muted-foreground">
										{cadenceLabel} · {planStatusLabel({ isTrialing, cancelAt, expired })}
									</div>
								) : (
									<PlanMeta
										isTrialing={isTrialing}
										trialEnd={trialEnd}
										cancelAt={cancelAt}
										currentPeriodEnd={currentPeriodEnd}
										expired={expired}
									/>
								)}
							</div>
							{headlineAmount ? (
								<div className="text-right">
									<div className="text-2xl font-extrabold tabular-nums text-foreground">{headlineAmount}</div>
									<div className="text-xs text-muted-foreground">{subtext ?? `per ${cadence} + VAT`}</div>
								</div>
							) : null}
						</div>
						<PricingTimeline timeline={timeline} />
						{isTrialing && !hasPaymentMethod ? (
							<div className="space-y-3 border-t border-border/60 pt-4">
								<div className="flex items-center justify-between gap-4">
									<div>
										<div className="text-sm font-bold text-foreground">Amount due today</div>
										<div className="text-xs text-muted-foreground">{"You're in your free trial"}</div>
									</div>
									<div className="text-right">
										<div className="text-base font-bold text-foreground">
											{money(0, planCurrency)} {(planCurrency ?? 'gbp').toUpperCase()}
										</div>
										<div className="text-[11px] text-muted-foreground">plus VAT where applicable</div>
									</div>
								</div>
								{onAddCard ? (
									<>
										<Button
											variant="action"
											onClick={onAddCard}
											className="h-auto w-full rounded-xl py-3 text-sm font-semibold"
										>
											Stay with Unify
										</Button>
										<div className="text-center text-[11px] text-muted-foreground">
											Change or cancel your plan at any time.
										</div>
									</>
								) : null}
							</div>
						) : null}
					</div>
				)}
			</SoftCard>
		</section>
	);
}

function PlanBadge({ proAccessIncluded, displayPlan }) {
	if (proAccessIncluded) {
		return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">✦ Free Pro upgrade</span>;
	}
	if (displayPlan === 'pro') {
		return (
			<Badge variant="info" className="gap-1">
				<Sparkles className="h-3 w-3" />
				Pro
			</Badge>
		);
	}
	return null;
}

function PlanMeta({ isTrialing, trialEnd, cancelAt, currentPeriodEnd, expired }) {
	if (expired) {
		return <div className="text-xs text-muted-foreground">Subscription paused — add a card to resume.</div>;
	}
	if (cancelAt) {
		return (
			<div className="text-xs text-muted-foreground">
				Ends {format(new Date(cancelAt), 'd MMM yyyy')} — you keep full access until then.
			</div>
		);
	}
	if (isTrialing && trialEnd) {
		return <div className="text-xs text-muted-foreground">Free until {format(new Date(trialEnd), 'd MMM yyyy')}.</div>;
	}
	if (currentPeriodEnd) {
		return <div className="text-xs text-muted-foreground">Renews {format(new Date(currentPeriodEnd), 'd MMM yyyy')}.</div>;
	}
	return null;
}

function NoSubscriptionState({ onChangePlan }) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-border bg-muted/40 p-4">
			<div className="min-w-0">
				<div className="text-sm font-semibold text-foreground">No active subscription</div>
				<div className="text-xs text-muted-foreground">Pick Starter or Pro to switch Unify on for this location.</div>
			</div>
			{onChangePlan ? (
				<Button variant="action" onClick={onChangePlan}>
					Subscribe
				</Button>
			) : null}
		</div>
	);
}
